// Every string AuctionLights can render, and the shape a caller may replace
// them with.
//
// The mechanism is BidCard's exactly — one object of finished text in one
// language, merged over these defaults key by key, with an UNKNOWN key rejected
// because that is the failure that renders English forever with nothing saying
// why. The reasoning is written out once, next door in bidCardTranslations.js.
//
// There is no `formatMoney` half here: the row shows no money, so the `i18n`
// prop is `{ translations }` alone.
//
// It lives beside the component rather than inside it for bidCardTranslations'
// own reason: a test that checks every locale keeps the `{lights}` placeholder
// has to import the same object the validator reads. Deliberately NOT exported
// from the package barrel: the catalog gate holds that a barrel exports
// components and nothing else.

import { unreadTranslationKeys as unreadKeysAgainst } from './bidCardTranslations';

export { interpolate } from './bidCardTranslations';

/**
 * The English defaults, frozen.
 *
 * `{lights}` is filled by the component with the drawn lights' names. A
 * translation may move it around its sentence; it may not invent a placeholder,
 * since the component only substitutes the names it knows.
 */
export const AUCTION_LIGHTS_TRANSLATIONS = Object.freeze({
  // Visually hidden. Colored letters are not self-describing.
  lights: 'Lights: {lights}',
  // All five codes. Every surface that reads this component is handed white
  // and draws it, so the sentence has to be able to say so.
  lightNames: Object.freeze({ R: 'Red', G: 'Green', Y: 'Yellow', B: 'Blue', W: 'White' }),
});

/**
 * The keys in `translations` that AuctionLights would not read, as dotted
 * paths. The walk itself is BidCard's — it already takes the defaults object
 * as an argument, so this passes its own rather than owning a second copy.
 */
export const unreadTranslationKeys = (translations) =>
  unreadKeysAgainst(translations, AUCTION_LIGHTS_TRANSLATIONS);

/** One language's text over the English defaults, key by key. */
export function mergeTranslations(translations) {
  if (!translations) return AUCTION_LIGHTS_TRANSLATIONS;

  return {
    ...AUCTION_LIGHTS_TRANSLATIONS,
    ...translations,
    lightNames: { ...AUCTION_LIGHTS_TRANSLATIONS.lightNames, ...(translations.lightNames ?? {}) },
  };
}

/**
 * The `i18n` prop's validator: `{ translations }`, optional.
 *
 * Closed on the outer object too — `translation` for `translations` fails
 * exactly the way a mis-keyed string does, silently and in English.
 */
export function isValidAuctionLightsI18n(i18n) {
  if (i18n === null || typeof i18n !== 'object') return false;
  if (Object.keys(i18n).some((key) => key !== 'translations')) return false;
  if (i18n.translations === undefined) return true;
  return unreadTranslationKeys(i18n.translations).length === 0;
}
